<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CustomToolForm from './CustomToolForm.vue';
import ToolTemplateSelector from './ToolTemplateSelector.vue';
import ToolTemplateConfig from './ToolTemplateConfig.vue';

const props = defineProps({
  selectedTool: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});

const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

// View state: 'select_template' | 'configure_template' | 'custom_form'
const currentView = ref(props.type === 'edit' ? 'custom_form' : 'select_template');
const selectedTemplate = ref(null);

const updateTool = toolDetails =>
  store.dispatch('captainCustomTools/update', {
    id: props.selectedTool.id,
    ...toolDetails,
  });

const i18nKey = computed(
  () => `CAPTAIN.CUSTOM_TOOLS.${props.type.toUpperCase()}`
);

const dialogTitle = computed(() => {
  if (props.type === 'edit') return t(`${i18nKey.value}.TITLE`);
  if (currentView.value === 'select_template')
    return t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.SELECT_TITLE');
  if (currentView.value === 'configure_template')
    return t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.CONFIGURE_TITLE');
  return t(`${i18nKey.value}.TITLE`);
});

const dialogDescription = computed(() => {
  if (currentView.value === 'select_template')
    return t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.SELECT_SUBTITLE');
  if (currentView.value === 'configure_template')
    return t('CAPTAIN.CUSTOM_TOOLS.TEMPLATE.CONFIGURE_SUBTITLE');
  return t('CAPTAIN.CUSTOM_TOOLS.FORM_DESCRIPTION');
});

const createTool = toolDetails =>
  store.dispatch('captainCustomTools/create', toolDetails);

const handleSelectTemplate = template => {
  selectedTemplate.value = template;
  currentView.value = 'configure_template';
};

const handleGoToCustomForm = () => {
  selectedTemplate.value = null;
  currentView.value = 'custom_form';
};

const handleBackToSelector = () => {
  selectedTemplate.value = null;
  currentView.value = 'select_template';
};

const handleTemplateSubmit = async ({ templateId, configurableValues }) => {
  try {
    await store.dispatch('captainCustomTools/createFromTemplate', {
      templateId,
      configurableValues,
    });
    useAlert(t('CAPTAIN.CUSTOM_TOOLS.CREATE.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) ||
      t('CAPTAIN.CUSTOM_TOOLS.CREATE.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSubmit = async updatedTool => {
  try {
    if (props.type === 'edit') {
      await updateTool(updatedTool);
    } else {
      await createTool(updatedTool);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    dialogRef.value.close();
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  currentView.value = props.type === 'edit' ? 'custom_form' : 'select_template';
  selectedTemplate.value = null;
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="handleClose"
  >
    <!-- Step 1: Select template or custom -->
    <ToolTemplateSelector
      v-if="currentView === 'select_template'"
      @select="handleSelectTemplate"
      @custom="handleGoToCustomForm"
    />

    <!-- Step 2: Configure template -->
    <ToolTemplateConfig
      v-else-if="currentView === 'configure_template' && selectedTemplate"
      :template="selectedTemplate"
      @submit="handleTemplateSubmit"
      @back="handleBackToSelector"
    />

    <!-- Classic custom tool form -->
    <CustomToolForm
      v-else
      :mode="type"
      :tool="selectedTool"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />

    <template #footer />
  </Dialog>
</template>
